import i18n, { type TFunction } from 'i18next';
import * as React from 'react';
import type { Dispatch } from 'redux';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import type { CoaAccountRow } from 'store/chart-of-accounts/models';
import { turnDrawerOn, turnModalOff, turnModalOn } from 'store/app/actions';
import { DrawerType, WindowType } from 'store/app/enums';
import type { DrawerData } from 'store/app/types';
import type { Organisation } from 'store/organisation/models';
import {
  saveRuleRequest,
  updateRuleRequest,
} from 'store/receipt-rules/actions';
import { CopyRuleToEntityModal } from './CopyRuleToEntityModal';
import { showsCoaAccountCodeColumn } from 'utils/get-provider-type';
import { buildAccountNameOptionsFromCoa } from '../transactions-tab/account-display';
import { buildContactNameValueOptions } from '../transactions-tab/contact-display';
import { buildTransactionItemOptions } from '../transactions-tab/item-display';
import { getSourceTypeOptions } from '../transactions-tab/constants';
import {
  buildTaxNameValueOptions,
} from '../transactions-tab/tax-display';
import {
  buildTrackingOptionFieldOptions,
  shouldIncludeTrackingCategoryFields,
} from '../transactions-tab/tracking-display';
import {
  buildEmptyRuleDraft,
  InterpretationRuleModal,
} from './InterpretationRuleModal';
import {
  cloneRuleForCopy,
  createRuleModalInstanceKey,
  applyParsedFieldValuesToActions,
  applyParsedFieldValuesToConditions,
  type RuleAuthoringMode,
} from './rule-modal-helpers';
import { InterpretationRulesDrawer } from './InterpretationRulesDrawer';
import type {
  InterpretationRule,
  InterpretationRuleFieldOptions,
  InterpretationRuleDraft,
} from './types';
import type { Contact } from 'store/vat-checker/models';
import type { TrackingCategory } from 'store/organisation/models';
import type { Country } from 'store/common/models';
import {
  getCountryLabelFromCountries,
  withoutEuropeanUnion,
} from 'i18n/countryLabel';
import type { ReceiptEntityStatistics } from 'store/organisation/models';
import { entityHasCaptureEnabled } from 'utils/entity-capture-column';

export function buildCountryValueOptions(
  countries?: Country[],
  language = 'en'
): { label: string; value: string }[] {
  const selectableCountries = withoutEuropeanUnion(countries);
  return [...selectableCountries]
    .sort((a, b) =>
      getCountryLabelFromCountries(
        a.iso,
        selectableCountries,
        language,
        a.name
      ).localeCompare(
        getCountryLabelFromCountries(
          b.iso,
          selectableCountries,
          language,
          b.name
        )
      )
    )
    .map((country) => ({
      label: getCountryLabelFromCountries(
        country.iso,
        selectableCountries,
        language,
        country.name
      ),
      value: country.iso,
    }));
}

export function buildInterpretationRuleFieldOptions(params: {
  accountNameOptions?: { label: string; value: string }[];
  taxNameOptions?: { label: string; value: string }[];
  contactNameOptions?: { label: string; value: string }[];
  countryOptions?: { label: string; value: string }[];
  itemOptions?: { label: string; value: string }[];
  trackingCategories?: TrackingCategory[];
  showTrackingCategoryFields?: boolean;
  sourceTypeOptions?: { label: string; value: string }[];
}): InterpretationRuleFieldOptions {
  const showTrackingCategoryFields =
    params.showTrackingCategoryFields ??
    params.trackingCategories !== undefined;

  const tracking = buildTrackingOptionFieldOptions(
    showTrackingCategoryFields ? params.trackingCategories : undefined
  );

  return {
    fieldOptions: [],
    accountOptions: params.accountNameOptions,
    taxOptions: params.taxNameOptions,
    contactNameOptions: params.contactNameOptions,
    countryOptions: params.countryOptions,
    itemOptions: params.itemOptions,
    trackingOption1Options: tracking.trackingOption1Options,
    trackingOption2Options: tracking.trackingOption2Options,
    trackingFieldLabels: tracking.trackingFieldLabels,
    showTrackingCategoryFields,
    sourceTypeOptions: params.sourceTypeOptions,
  };
}

export function buildInterpretationRuleFieldOptionsFromOrganisation(
  organisation:
    | Pick<
        Organisation,
        | 'chartOfAccounts'
        | 'taxRates'
        | 'taxRatesUnique'
        | 'providerName'
        | 'trackingCategories'
        | 'transactionItems'
      >
    | undefined,
  contacts?: Contact[],
  countries?: Country[],
  language = 'en'
): InterpretationRuleFieldOptions {
  const chartOfAccounts = (organisation?.chartOfAccounts ??
    []) as CoaAccountRow[];
  const organisationTaxRates =
    organisation?.taxRatesUnique && organisation.taxRatesUnique.length > 0
      ? organisation.taxRatesUnique
      : organisation?.taxRates ?? [];
  const showTrackingCategoryFields =
    shouldIncludeTrackingCategoryFields(organisation);

  return buildInterpretationRuleFieldOptions({
    accountNameOptions: buildAccountNameOptionsFromCoa(
      chartOfAccounts,
      showsCoaAccountCodeColumn(organisation?.providerName)
    ),
    taxNameOptions: buildTaxNameValueOptions(organisationTaxRates),
    contactNameOptions: buildContactNameValueOptions(contacts),
    countryOptions: buildCountryValueOptions(countries, language),
    itemOptions: buildTransactionItemOptions(
      organisation?.transactionItems ?? []
    ),
    trackingCategories: organisation?.trackingCategories,
    showTrackingCategoryFields,
    sourceTypeOptions: getSourceTypeOptions(),
  });
}

export function filterCopyRuleTargetOrganisations(
  organisations: Organisation[] | undefined,
  sourceOrganisationId: string,
  statsByConnectionId?: Map<string, ReceiptEntityStatistics>
): Organisation[] {
  return (organisations ?? [])
    .filter((org) => org.uuid !== sourceOrganisationId)
    .filter((org) =>
      entityHasCaptureEnabled(org, { statsByConnectionId })
    )
    .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));
}

export function openCopyRuleToEntityPicker(params: {
  dispatch: Dispatch;
  t: TFunction;
  sourceOrganisationId: string;
  rule: InterpretationRule;
  organisations: Organisation[];
  countries?: Country[];
  statsByConnectionId?: Map<string, ReceiptEntityStatistics>;
}): void {
  const {
    dispatch,
    t,
    sourceOrganisationId,
    rule,
    organisations,
    countries,
    statsByConnectionId,
  } = params;
  const targetOrganisations = filterCopyRuleTargetOrganisations(
    organisations,
    sourceOrganisationId,
    statsByConnectionId
  );

  dispatch(
    turnModalOn({
      title: t('organisation.extraction-rules.copy-to-title'),
      type: WindowType.WARNING,
      component: (
        <CopyRuleToEntityModal
          rule={rule}
          targetOrganisations={targetOrganisations}
          onContinue={(targetOrganisationId) => {
            dispatch(turnModalOff());
            openCopyRuleReviewModal({
              dispatch,
              t,
              sourceRule: rule,
              targetOrganisationId,
              organisations,
              countries,
            });
          }}
        />
      ),
    })
  );
}

export function openCopyRuleReviewModal(params: {
  dispatch: Dispatch;
  t: TFunction;
  sourceRule: InterpretationRule;
  targetOrganisationId: string;
  organisations: Organisation[];
  countries?: Country[];
}): void {
  const {
    dispatch,
    t,
    sourceRule,
    targetOrganisationId,
    organisations,
    countries,
  } = params;
  const targetOrg = organisations.find((org) => org.uuid === targetOrganisationId);
  if (!targetOrg) return;

  const fieldOptions = buildInterpretationRuleFieldOptionsFromOrganisation(
    targetOrg,
    undefined,
    countries,
    i18n.language
  );
  const cloned = cloneRuleForCopy(
    sourceRule,
    t('organisation.extraction-rules.copied')
  );
  const draft = buildEmptyRuleDraft(
    {
      ...cloned,
      conditions: applyParsedFieldValuesToConditions(
        cloned.conditions ?? [],
        fieldOptions
      ),
      actions: applyParsedFieldValuesToActions(
        cloned.actions ?? [],
        fieldOptions
      ),
    },
    t
  );

  dispatch(
    turnModalOn({
      title: t('organisation.extraction-rules.copy-to-review-title', {
        entityName: targetOrg.friendlyName,
      }),
      type: WindowType.WARNING,
      fullHeight: true,
      component: (
        <InterpretationRuleModal
          key={createRuleModalInstanceKey()}
          organisationId={targetOrganisationId}
          fieldOptions={fieldOptions}
          initialRule={draft}
          initialMode="build"
          expandAllSteps={true}
          onSave={async (rule) => {
            dispatch(saveRuleRequest(targetOrganisationId, rule));
          }}
        />
      ),
    })
  );
}

export function openInterpretationRulesDrawer(params: {
  dispatch: Dispatch;
  t: TFunction;
  organisationId: string;
}): void {
  const { dispatch, t, organisationId } = params;
  dispatch(
    turnDrawerOn({
      title: (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <IconFuzzyMapping
            aria-hidden
            style={{ width: 20, height: 20, flexShrink: 0, color: 'var(--color-primary, #41aaaf)' }}
          />
          {t('organisation.extraction-rules.drawer-title')}
        </span>
      ),
      subtitle: t('organisation.extraction-rules.drawer-intro'),
      type: DrawerType.INTERPRETATION_RULES,
      component: (
        <InterpretationRulesDrawer organisationId={organisationId} />
      ),
    } satisfies DrawerData)
  );
}

export function openInterpretationRuleModal(params: {
  dispatch: Dispatch;
  t: TFunction;
  organisationId: string;
  fieldOptions: InterpretationRuleFieldOptions;
  initialRule?: Partial<InterpretationRule>;
  initialMode?: RuleAuthoringMode;
  expandAllSteps?: boolean;
  onSaved?: () => void;
}): void {
  const {
    dispatch,
    t,
    organisationId,
    fieldOptions,
    initialRule,
    initialMode,
    expandAllSteps,
    onSaved,
  } = params;

  dispatch(
    turnModalOn({
      title: initialRule?.id
        ? t('organisation.extraction-rules.edit-rule-title')
        : t('organisation.extraction-rules.create-rule-title'),
      type: WindowType.WARNING,
      fullHeight: true,
      component: (
        <InterpretationRuleModal
          key={createRuleModalInstanceKey()}
          organisationId={organisationId}
          fieldOptions={fieldOptions}
          initialRule={initialRule}
          initialMode={initialMode}
          expandAllSteps={expandAllSteps}
          onSave={async (rule) => {
            if (rule.id) {
              dispatch(updateRuleRequest(organisationId, rule, rule.id));
            } else {
              dispatch(saveRuleRequest(organisationId, rule));
            }
            onSaved?.();
          }}
        />
      ),
    })
  );
}

export function openInterpretationRuleModalFromDraft(params: {
  dispatch: Dispatch;
  t: TFunction;
  organisationId: string;
  fieldOptions: InterpretationRuleFieldOptions;
  draft: Partial<InterpretationRuleDraft>;
  onSaved?: () => void;
}): void {
  const normalizedDraft: Partial<InterpretationRuleDraft> = {
    ...params.draft,
    conditions: applyParsedFieldValuesToConditions(
      params.draft.conditions ?? [],
      params.fieldOptions
    ),
    actions: applyParsedFieldValuesToActions(
      params.draft.actions ?? [],
      params.fieldOptions
    ),
  };

  openInterpretationRuleModal({
    ...params,
    initialRule: buildEmptyRuleDraft(normalizedDraft, params.t),
    initialMode: 'build',
    expandAllSteps: true,
  });
}
